import React, { ReactNode, MouseEvent, useEffect, useRef } from 'react'
import { Badge } from '@enamelai/design-system'
import { useLocation, useNavigate } from 'react-router-dom'
import { Form } from 'react-bootstrap'
import InputField from '@components/common/InputField/InputField.component'
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons'
import { useAppSelector } from '@store/hooks'
import './MessageCenterChatSidePanelStyle.css'
import useChatSidePanel from './useChatSidePanel'
import { getFirstLineFromHTML } from '@utils/messageFormatter.util'
import { getCallLog, messageCountsAsUnread } from '@utils/chat.util'
import { TooltipComponent } from '@syncfusion/ej2-react-popups'
import { DropDownButtonComponent } from '@syncfusion/ej2-react-splitbuttons'
import { BeforeItemRenderEventArgs } from '@syncfusion/ej2-react-dropdowns'
import Avatar from '@components/common/Avatar/Avatar.component'
import { useNotificationToast } from '@context/NotificationToastContext'
import { showCommunicationLogNotFoundToast } from '@utils/patientProfileCommunicationNavigation.util'

const ChatSidePanel: React.FC = () => {
  const { ...sidePanelHooks } = useChatSidePanel()

  // Chat notification deep-link consumer.
  //
  // App.tsx navigates to `/message` with `location.state = { tab: "chat",
  // conversationId, ... }`. Message.page handles the tab switch via
  // SET_ACTIVE_TAB, which is what mounts this component. From there we wait
  // for the conversation list (`state.chat.data`) to be populated — the
  // reducer behind `updateSelectedMessage` resolves the id by looking it up
  // in `state.data` and silently leaves `selectedMessage` null if the
  // conversation isn't present, so dispatching too early would no-op.
  //
  // Once the conversation is in the list we call
  // `sidePanelHooks.handleSelectConversation(id)` — the same path the user
  // takes when clicking a sidebar row. That dispatches `updateSelectedMessage`
  // (which renders <ChatPreview/>) AND bulk-marks unread messages as read, so
  // the deep-link behaves identically to a manual click.
  //
  // After applying we clear `location.state` so a remount or browser
  // back/forward doesn't replay the open. The applied-ref resets when
  // `state.conversationId` clears so a re-click of the same notification
  // works.
  const location = useLocation()
  const navigate = useNavigate()
  // Subscribe to the raw Redux array. We intentionally do NOT splash `?? []`
  // here — that would create a new array reference on every render where
  // `data` is undefined, and break the `conversations` dep on the deep-link
  // effect below (it would re-fire every render). The effect handles the
  // undefined case itself.
  const conversations = useAppSelector((state) => state.chat.data)
  const chatLoading = useAppSelector((state) => state.chat.loading)
  const { showToast } = useNotificationToast()
  const appliedDeepLinkConversationIdRef = useRef<string | null>(null)
  const conversationNotFoundToastShownRef = useRef(false)
  // `useChatSidePanel` returns a fresh `handleSelectConversation` on every
  // render (it's a plain function, not useCallback'd), so we mirror it into
  // a ref to keep our deep-link effect's deps narrow. Without this, listing
  // `sidePanelHooks` as a dep would re-fire the effect every render — and
  // omitting it from deps would close over a stale function.
  const panelRef = useRef(sidePanelHooks)
  panelRef.current = sidePanelHooks
  const handleSelectConversationRef = useRef(
    sidePanelHooks.handleSelectConversation,
  )
  useEffect(() => {
    handleSelectConversationRef.current =
      sidePanelHooks.handleSelectConversation
  })

  useEffect(() => {
    const routeState = location.state as {
      conversationId?: string
      chatMessageId?: string
      previousPage?: unknown
    } | null
    const conversationId = routeState?.conversationId
    if (!conversationId) {
      appliedDeepLinkConversationIdRef.current = null
      conversationNotFoundToastShownRef.current = false
      return
    }
    if (appliedDeepLinkConversationIdRef.current === conversationId) return
    if (chatLoading || conversations === undefined) return

    const match = conversations?.find(
      (c) => c.chat_conversation_id === conversationId,
    )

    const previousPage = routeState?.previousPage
    const chatMessageId = routeState?.chatMessageId

    const clearConversationDeepLink = (): void => {
      navigate(location.pathname, {
        replace: true,
        state: {
          ...(previousPage ? { previousPage } : {}),
          ...(chatMessageId ? { chatMessageId } : {}),
        },
      })
    }

    if (!match) {
      if (!conversationNotFoundToastShownRef.current) {
        conversationNotFoundToastShownRef.current = true
        showCommunicationLogNotFoundToast(showToast, 'SMS conversation')
      }
      appliedDeepLinkConversationIdRef.current = conversationId
      clearConversationDeepLink()
      return
    }

    // Match the inbox/archive view before selecting, so the selection isn't cleared.
    if (panelRef.current.showArchive !== Boolean(match.is_archived)) {
      panelRef.current.setShowArchive(Boolean(match.is_archived))
      return
    }
    panelRef.current.handleFilter('name', '')
    panelRef.current.handleFilter('channel', match.channel)
    appliedDeepLinkConversationIdRef.current = conversationId
    handleSelectConversationRef.current(conversationId)
    clearConversationDeepLink()
  }, [
    location.state,
    location.pathname,
    conversations,
    chatLoading,
    sidePanelHooks.showArchive,
    navigate,
    showToast,
  ])

  const beforeItemRender = (
    args: BeforeItemRenderEventArgs,
    element: any,
  ): void => {
    const contactGroup =
      sidePanelHooks.contactGroups && sidePanelHooks.contactGroups.length
        ? sidePanelHooks.contactGroups.find(
            (c) => c.contact_group_id === args.item.id,
          )
        : undefined
    let index = -1
    if (contactGroup && contactGroup.contacts)
      index = contactGroup.contacts.findIndex(
        (c) =>
          c.contact_id ===
          (element.chat_participant &&
          element.chat_participant.length === 1 &&
          element.chat_participant[0].contact
            ? element.chat_participant[0].contact.contact_id
            : ''),
      )
    if (element.chat_participant && element.chat_participant.length > 1)
      index = -1

    const isBlocked =
      sidePanelHooks.blockedUsers.find(
        (blockedUser) =>
          blockedUser.contact_id ===
          (element.chat_participant &&
          element.chat_participant.length === 1 &&
          element.chat_participant[0].contact
            ? element.chat_participant[0].contact.contact_id
            : ''),
      ) !== undefined

    if (
      ((args.item.id === 'unread' || args.item.id === 'read') &&
        element.is_archived) ||
      (args.item.id === 'group' &&
        ((element.chat_participant &&
          element.chat_participant.length === 1 &&
          !element.chat_participant[0].contact) ||
          (element.chat_participant && element.chat_participant.length > 1))) ||
      (args.item.id === 'block' &&
        element.chat_participant &&
        element.chat_participant.length > 1) ||
      index !== -1
    ) {
      args.element.classList.add('e-disabled')
    }

    if (args.item.text === 'Delete chat') {
      args.element.classList.add('text-default')
    }
  }

  const highlightText = (text: string, highlight: string): ReactNode => {
    if (text.length && highlight.length) {
      const regExp = new RegExp(`(${highlight})`, 'gi')
      const parts = text.split(regExp)
      return parts.map((part, index) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <span key={index} className="search-highlight">
            {part}
          </span>
        ) : (
          part
        ),
      )
    } else {
      return <span>{text}</span>
    }
  }

  const renderMainCheckBox = (): JSX.Element => {
    return sidePanelHooks.selectedConversations ? (
      <CheckBoxComponent
        id="side-panel-main-checkbox"
        checked={false}
        change={sidePanelHooks.handleMainCheckboxChange}
      />
    ) : (
      <></>
    )
  }

  return (
    <div className="chat-sp">
      <div className="csp-filter">
        <InputField
          placeholder="Search"
          value={sidePanelHooks.filteredText}
          leftIcon={<i className="fa-solid fa-magnifying-glass"></i>}
          onChange={(e) => sidePanelHooks.handleFilter('name', e.target.value)}
        />
        <div className="channel-container">
          <span className="csp-filter-subheader">Channel</span>
          <Form.Select
            value={sidePanelHooks.selectedChannel}
            onChange={(e) =>
              sidePanelHooks.handleFilter('channel', e.target.value)
            }
            className="csp-cc-select"
            disabled
          >
            {sidePanelHooks.channelSelection.map((data, index) => {
              return (
                <option key={index} value={data.key}>
                  {data.text}
                </option>
              )
            })}
          </Form.Select>
        </div>
      </div>
      <div className="csp-convo-list">
        {sidePanelHooks.archivedList.length > 0 && (
          <button
            type="button"
            className="unstyled-button show-archive-button"
            onClick={sidePanelHooks.onClickArchiveButton}
          >
            <span className="left-side">
              <span
                className={
                  sidePanelHooks.showArchive
                    ? 'mgc_left_fill'
                    : 'mgc_archive_line'
                }
              />
              <span>
                {sidePanelHooks.showArchive ? 'Active chats' : 'Archived chat'}
              </span>
            </span>
            {!sidePanelHooks.showArchive && (
              <span className="archive-count">
                {sidePanelHooks.archivedList.length}
              </span>
            )}
          </button>
        )}
        {sidePanelHooks.selectedConversations &&
          !sidePanelHooks.showArchive && (
            <div className="select-multiple-container">
              <div className="select-multiple-content">
                <span className="smc-count">
                  {renderMainCheckBox()}
                  {sidePanelHooks.selectedConversations.length}
                  {sidePanelHooks.selectedConversations.length > 1
                    ? ' chats '
                    : ' chat '}
                  selected
                </span>
                <span className="smc-actions">
                  {sidePanelHooks.multipleSelectAction.map((action) => {
                    const isDisabled =
                      action.id === 'new-chat' &&
                      sidePanelHooks.selectedConversations &&
                      (() => {
                        // Check if selected conversations have different channels
                        const selectedConversationData =
                          sidePanelHooks.selectedConversations
                            .map((id) =>
                              sidePanelHooks.data.find(
                                (convo) => convo.chat_conversation_id === id,
                              ),
                            )
                            .filter(Boolean)

                        if (selectedConversationData.length <= 0) return false

                        const firstChannel =
                          selectedConversationData[0]?.channel
                        return selectedConversationData.some(
                          (convo) => convo?.channel !== firstChannel,
                        )
                      })()
                    return (
                      <TooltipComponent
                        content={
                          isDisabled
                            ? 'This action is unavailable because the selected participants are in different channels. Please select participants from the same channel to proceed.'
                            : action.label
                        }
                        key={action.id}
                        cssClass="mcc-smc-action-tooltip"
                      >
                        <button
                          type="button"
                          key={action.id}
                          id={action.id}
                          aria-label={action.label}
                          className={`unstyled-button ${action.icon}${
                            isDisabled ? ' disabled' : ''
                          }`}
                          onClick={sidePanelHooks.handleMultipleSelectAction}
                          data-bs-toggle="tooltip"
                        />
                      </TooltipComponent>
                    )
                  })}
                </span>
              </div>
            </div>
          )}
        <div className="csp-convo-list-inner">
          {sidePanelHooks.data.length === 0 ? (
            <p className="csp-filter-subheader mt-3 fs-14 fw-medium m-0 text-center">
              No results found
            </p>
          ) : (
            sidePanelHooks.data.map((element) => {
              const tempChat = element.chat_message
              const tempParticipant = element.chat_participant
              const _participant = tempParticipant
                ?.map((p) => p.username ?? p.phone_number)
                .join(', ')
              const unreadCount = tempChat.filter(messageCountsAsUnread).length

              if (tempChat) {
                const conversationLength = tempChat.length
                const displayConversation = sidePanelHooks.getMessageDisplay(
                  tempChat,
                  sidePanelHooks.filteredText,
                )

                let displayMessage = !displayConversation
                  ? 'Conversation started'
                  : `${displayConversation.sender?.first_name ?? 'Unknown number'}: ${displayConversation.content}`

                if (
                  displayConversation &&
                  displayConversation.content.includes('[CHAT-CALL-LOG]::{') &&
                  tempParticipant &&
                  tempParticipant.length > 0 &&
                  tempParticipant[0].username
                ) {
                  displayMessage = getCallLog(
                    displayConversation.content,
                    displayConversation.sender?.first_name ?? '',
                    tempParticipant[0].username,
                  )
                }

                const showCheckbox =
                  !sidePanelHooks.showArchive &&
                  (sidePanelHooks.hoverConversation ===
                    element.chat_conversation_id ||
                    sidePanelHooks.selectedConversations)

                const showKebabBtn =
                  !sidePanelHooks.showArchive &&
                  sidePanelHooks.hoverConversation ===
                    element.chat_conversation_id

                const isBlocked =
                  sidePanelHooks.blockedUsers.find(
                    (blockedUser) =>
                      blockedUser.contact_id ===
                      (tempParticipant &&
                      tempParticipant.length === 1 &&
                      tempParticipant[0].contact
                        ? tempParticipant[0].contact.contact_id
                        : ''),
                  ) !== undefined

                const hasUnreadMessages = tempChat.some(messageCountsAsUnread)

                const dropdownItems = [
                  element.is_archived
                    ? {
                        text: 'Unarchive chat',
                        iconCss: 'mgc_unarchive_line',
                        id: 'unarchive',
                      }
                    : {
                        text: 'Archive chat',
                        iconCss: 'mgc_archive_line',
                        id: 'archive',
                      },
                  element.is_pinned
                    ? {
                        text: 'Unpin chat',
                        iconCss: 'mgc_pin_line',
                        id: 'unpin',
                      }
                    : {
                        text: 'Pin chat',
                        iconCss: 'mgc_pin_fill',
                        id: 'pin',
                      },
                  hasUnreadMessages
                    ? {
                        text: 'Mark read',
                        iconCss: 'mgc_mail_fill',
                        id: 'read',
                      }
                    : {
                        text: 'Mark unread',
                        iconCss: 'mgc_mail_open_fill',
                        id: 'unread',
                      },
                  isBlocked
                    ? {
                        text: 'Unblock chat',
                        iconCss: 'mgc_user_follow_fill',
                        id: 'unblock',
                      }
                    : {
                        text: 'Block chat',
                        iconCss: 'mgc_user_forbid_fill',
                        id: 'block',
                      },
                  {
                    text: 'Delete chat',
                    iconCss: 'mgc_delete_2_fill',
                    id: 'delete',
                  },
                ]

                return (
                  <button
                    className={`convo-list-item ${element.chat_conversation_id === sidePanelHooks.selectedMessageState?.chat_conversation_id ? 'active' : 'inactive'}`}
                    key={element.chat_conversation_id}
                    onClick={() => {
                      if (element.chat_conversation_id)
                        sidePanelHooks.handleSelectConversation(
                          element.chat_conversation_id,
                        )
                    }}
                    onMouseEnter={() => {
                      if (element.chat_conversation_id)
                        sidePanelHooks.handleMouseEnter(
                          element.chat_conversation_id,
                        )
                    }}
                    onMouseLeave={() =>
                      sidePanelHooks.handleMouseLeave(
                        element.chat_conversation_id,
                      )
                    }
                  >
                    {showCheckbox && (
                      <CheckBoxComponent
                        key="checkbox"
                        checked={sidePanelHooks.selectedConversations?.includes(
                          element.chat_conversation_id as string,
                        )}
                        onClick={(args: MouseEvent<HTMLElement>) =>
                          args.stopPropagation()
                        }
                        change={() =>
                          sidePanelHooks.handleClickConversationCheckbox(
                            element.chat_conversation_id as string,
                          )
                        }
                      />
                    )}
                    <div className="cli-component">
                      <div className="profile-wrapper">
                        {!tempParticipant ||
                        tempParticipant.length === 0 ||
                        !tempParticipant[0]?.photo ? (
                          <span
                            key="no-photo"
                            className="mgc_user_3_fill no-profile-image"
                          />
                        ) : tempParticipant.length === 1 ? (
                          <Avatar
                            source={tempParticipant[0].photo}
                            name={
                              tempParticipant[0].username ??
                              tempParticipant[0].phone_number ??
                              element.name
                            }
                            width={40}
                            height={40}
                            key="single-photo"
                            style={{ borderRadius: '12px' }}
                          />
                        ) : tempParticipant.length >= 2 &&
                          tempParticipant[1].photo ? (
                          <div
                            className="multiple-participant-image"
                            key="multiphoto"
                          >
                            <Avatar
                              source={tempParticipant[0].photo}
                              name={
                                tempParticipant[0].username ??
                                tempParticipant[0].phone_number
                              }
                              style={{ borderRadius: '12px' }}
                            />
                            <Avatar
                              source={
                                tempParticipant[1].photo ??
                                tempParticipant[1].contact?.photo ??
                                'https://cdn-icons-png.flaticon.com/128/9131/9131478.png'
                              }
                              name={
                                tempParticipant[1].username ??
                                tempParticipant[1].phone_number
                              }
                              style={{ borderRadius: '12px' }}
                            />
                          </div>
                        ) : (
                          <span
                            key="no-photo-fallback"
                            className="mgc_user_3_fill no-profile-image"
                          />
                        )}
                      </div>

                      <div className="preview-details">
                        <div className="mcsp-row-1">
                          <span
                            className={`custom-chat-name ${unreadCount > 0 ? 'unread' : 'read'}`}
                          >
                            {highlightText(
                              element.name ?? _participant ?? '',
                              sidePanelHooks.filteredText,
                            )}
                          </span>
                          {element.assigned_team ? (
                            <Badge
                              mode="tinted"
                              color="cyan"
                              label={element.assigned_team}
                            />
                          ) : null}
                          <span className="mb-0 small-text">
                            {
                              sidePanelHooks.generateTimeDisplay(
                                tempChat.length > 0
                                  ? new Date(
                                      tempChat[
                                        conversationLength - 1
                                      ].timestamp,
                                    )
                                  : new Date(
                                      element.date_time_created_at ??
                                        new Date(),
                                    ),
                              )[0]
                            }
                          </span>
                        </div>
                        <div
                          className={`mcsp-row-2 ${unreadCount > 0 ? 'unread' : 'read'}`}
                        >
                          <span className="display-message">
                            <span className="last-chat">
                              {highlightText(
                                getFirstLineFromHTML(displayMessage),
                                sidePanelHooks.filteredText,
                              )}
                            </span>
                            {unreadCount > 0 && !element.is_archived && (
                              <span className="unread-count">
                                {unreadCount}
                              </span>
                            )}
                          </span>
                          {element.is_pinned && (
                            <span className="mgc_pin_fill" />
                          )}
                        </div>
                      </div>
                    </div>
                    {showKebabBtn && (
                      <span
                        className="mccsp-dropdown-wrapper"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <DropDownButtonComponent
                          items={dropdownItems}
                          cssClass="e-caret-hide mccsp-dropdown"
                          iconCss="ti ti-dots-vertical"
                          beforeItemRender={(args) => {
                            beforeItemRender(args, element)
                          }}
                          open={() => {
                            if (element.chat_conversation_id) {
                              sidePanelHooks.handleDropdownOpen(
                                element.chat_conversation_id,
                              )
                            }
                          }}
                          close={() => {
                            sidePanelHooks.handleDropdownClose()
                          }}
                          select={(args) => {
                            sidePanelHooks.handleDropdownAction(args, element)
                            sidePanelHooks.handleDropdownClose()
                          }}
                        />
                      </span>
                    )}
                  </button>
                )
              }
            })
          )}
        </div>
      </div>
    </div>
  )
}

export default ChatSidePanel
